import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  alpha,
  Box,
  IconButton,
  TableCell,
  Popper,
  Paper,
  ClickAwayListener,
  Typography,
  useTheme,
} from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { isDeepEqual } from 'components/utils';
import { errorMessageHtml } from 'lib/sanitizeHtml';
import CellValue, { type Cell, type ErasedEditable, type InlineEditorProps } from './CellValue';
import { useInlineEditing } from './InlineEditingContext';

type InlineEditableCellProps = {
  cell: Cell & { editable: ErasedEditable };
  cellSx: object;
  columnWidthPx?: number;
  onSaved?: () => void;
  rowId: string;
  columnName: string;
  editorKey?: string;
};

const NOT_CACHED = Symbol('not-cached');
// 24px pencil button plus the gap before it
const PENCIL_WIDTH_PX = 28;

const InlineEditableCell = ({
  cell,
  cellSx,
  columnWidthPx,
  onSaved,
  rowId,
  columnName,
  editorKey,
}: InlineEditableCellProps) => {
  const editable = cell.editable;
  const theme = useTheme();
  const hoverBorderColor = alpha(theme.palette.text.primary, 0.24);
  const inlineEditing = useInlineEditing();

  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState<unknown>(editable?.rawValue);
  const [cachedDisplayValue, setCachedDisplayValue] = useState<unknown>(NOT_CACHED);
  const [saveState, setSaveState] = useState<'idle' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const editSessionRef = useRef(0);
  const saveSeqRef = useRef(0);
  const isEditingRef = useRef(isEditing);
  const inFlightRef = useRef(false);
  const mountedRef = useRef(true);
  const cellRef = useRef<HTMLTableCellElement>(null);
  const paperRef = useRef<HTMLDivElement>(null);
  const enterEditModeRef = useRef<(() => void) | null>(null);
  const cancelEditRef = useRef<(() => void) | null>(null);

  const editValueRef = useRef(editValue);
  useEffect(() => {
    editValueRef.current = editValue;
  }, [editValue]);

  const cachedRef = useRef<unknown>(NOT_CACHED);
  const savedAgainstRef = useRef<unknown>(NOT_CACHED);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  useEffect(() => {
    isEditingRef.current = isEditing;
  }, [isEditing]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!editable || isEditingRef.current) return;
    if (savedAgainstRef.current === NOT_CACHED) return;
    if (!isDeepEqual(editable.rawValue, savedAgainstRef.current)) {
      setCachedDisplayValue(NOT_CACHED);
      cachedRef.current = NOT_CACHED;
      savedAgainstRef.current = NOT_CACHED;
    }
  }, [editable?.rawValue]);

  const currentValue = useCallback(
    () => (cachedRef.current !== NOT_CACHED ? cachedRef.current : editable?.rawValue),
    [editable?.rawValue],
  );

  const cellId = `${rowId}::${columnName}`;

  const enterEditMode = useCallback(() => {
    if (!editable) return;
    editSessionRef.current += 1;
    setEditValue(currentValue());
    setIsEditing(true);
    setSaveState('idle');
    setErrorMsg(null);
  }, [editable?.rawValue, currentValue]);

  enterEditModeRef.current = enterEditMode;

  const cancelEdit = useCallback(() => {
    editSessionRef.current += 1;
    setIsEditing(false);
    setErrorMsg(null);
    setEditValue(currentValue());
    inlineEditing.clearActiveCellId(cellId);
  }, [currentValue, cellId, inlineEditing]);

  cancelEditRef.current = cancelEdit;

  useEffect(() => {
    const openFn = () => enterEditModeRef.current?.();
    const commitFn = () => leaveEditorRef.current();
    inlineEditing.registerCell(cellId, openFn, commitFn);
    return () => {
      inlineEditing.unregisterCell(cellId);
      // Defense: if this cell was the active cell when it unmounted,
      // clear the ref so it doesn't block the table indefinitely.
      inlineEditing.clearActiveCellId(cellId);
    };
  }, [cellId]);

  useEffect(() => {
    if (isEditing) {
      inlineEditing.setActiveCellId(cellId);
    }
    return () => {
      // For editors with closeOnCommit: false, activeCellId is cleared
      // explicitly in commitValue/cancelEdit after the save resolves.
      if (editable?.closeOnCommit !== false) {
        inlineEditing.clearActiveCellId(cellId);
      }
    };
  }, [isEditing, cellId, editable?.closeOnCommit]);

  useEffect(() => {
    if (!isEditing) return;

    const handleDocKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!isEditingRef.current) return;

      const paperEl = paperRef.current;
      if (!paperEl) {
        cancelEditRef.current?.();
        return;
      }

      const activeEl = document.activeElement;
      if (
        paperEl.contains(activeEl) &&
        (activeEl instanceof HTMLInputElement || activeEl instanceof HTMLTextAreaElement)
      ) {
        e.preventDefault();
        activeEl.blur();
        e.stopPropagation();
      } else {
        cancelEditRef.current?.();
        e.stopPropagation();
      }
    };

    document.addEventListener('keydown', handleDocKeyDown, true);
    return () => document.removeEventListener('keydown', handleDocKeyDown, true);
  }, [isEditing]);

  // While editing, swallow clicks outside the editor + its portaled overlays
  // and outside this cell's own table. Commits via mousedown so it survives
  // cases where the click event never lands (e.g. MUI Modal/Backdrop unmounts
  // between mousedown and mouseup). stopPropagation+preventDefault prevents
  // the underlying element (a button on the page, a header link, etc.) from
  // firing its own handler. Same-table clicks fall through so cell-switching
  // keeps working.
  //
  // Race detail: committing on mousedown flips `isEditing` to false, which
  // unmounts this effect before the matching `click` event dispatches. To
  // ensure the underlying button doesn't fire on click, we install a
  // one-shot capture-phase click blocker that outlives the effect.
  useEffect(() => {
    if (!isEditing) return;
    const ownTable = cellRef.current?.closest('table') ?? null;
    const isOutside = (target: HTMLElement | null): boolean => {
      if (!target) return false;
      if (paperRef.current?.contains(target)) return false;
      if (target.closest('input[type="color"]')) return false;
      if (
        target.closest('.MuiAutocomplete-popper') ||
        target.closest('.MuiPickersPopper-root') ||
        target.closest('.MuiPickersLayout-root') ||
        target.closest('[role="menu"]') ||
        target.closest('[role="listbox"]')
      )
        return false;
      if (ownTable?.contains(target)) return false;
      return true;
    };
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      const activeElement = document.activeElement;
      if (activeElement instanceof HTMLInputElement && activeElement.type === 'color') {
        return;
      }
      if (!isOutside(target)) return;
      e.stopPropagation();
      e.preventDefault();
      if (e.type === 'mousedown') {
        const originalTarget = e.target as HTMLElement;
        // Block the click that will follow this mousedown, so it never
        // reaches the underlying button. The blocker is self-cleaning and
        // survives this effect's cleanup.
        const blockNextClick = (ce: MouseEvent) => {
          const clickTarget = ce.target as HTMLElement;
          // Only block clicks on the same element that triggered mousedown.
          if (clickTarget !== originalTarget && !originalTarget.contains(clickTarget)) {
            document.removeEventListener('click', blockNextClick, true);
            return;
          }
          ce.stopPropagation();
          ce.preventDefault();
          document.removeEventListener('click', blockNextClick, true);
        };
        document.addEventListener('click', blockNextClick, true);
        // Safety: drop the blocker if no click follows (e.g. user dragged off).
        window.setTimeout(() => {
          document.removeEventListener('click', blockNextClick, true);
        }, 500);
        void leaveEditorRef.current();
      }
    };
    document.addEventListener('mousedown', handler, true);
    document.addEventListener('click', handler, true);
    return () => {
      document.removeEventListener('mousedown', handler, true);
      document.removeEventListener('click', handler, true);
    };
  }, [isEditing]);

  const commitValue = useCallback(
    async (valueToSave: unknown): Promise<boolean> => {
      if (!editable || inFlightRef.current) return false;
      const sessionAtStart = editSessionRef.current;

      const cur = currentValue();
      if (isDeepEqual(valueToSave, cur)) {
        setIsEditing(false);
        setSaveState('idle');
        setErrorMsg(null);
        if (editable?.closeOnCommit === false) {
          // Keep activeCellId set to block other cells during the interaction
          // grace window, then clear it so it doesn't linger forever.
          setTimeout(() => {
            inlineEditing.clearActiveCellId(cellId);
          }, 300);
        }
        return true;
      }

      if (editable.validate) {
        const err = editable.validate(valueToSave);
        if (err) {
          setErrorMsg(err);
          return false;
        }
      }

      const previousCached = cachedRef.current;
      const previousDisplay = cachedDisplayValue;

      // Optimistic update — show new value immediately before API call
      cachedRef.current = valueToSave;
      setCachedDisplayValue(valueToSave);
      setIsEditing(false);
      setSaveState('idle');
      setErrorMsg(null);

      const seq = ++saveSeqRef.current;
      inFlightRef.current = true;
      inlineEditing.onSaveStart(cellId);

      try {
        await editable.onSave(valueToSave);
        if (!mountedRef.current) {
          inlineEditing.onSaveResolve(cellId, false);
          return false;
        }
        if (seq !== saveSeqRef.current || sessionAtStart !== editSessionRef.current) {
          inlineEditing.onSaveResolve(cellId, false);
          return false;
        }
        savedAgainstRef.current = valueToSave;
        onSavedRef.current?.();
        setTimeout(() => cellRef.current?.focus(), 0);
        if (editable?.closeOnCommit === false) {
          // Keep activeCellId set during the post-save grace window so that
          // a click on another cell in the same interaction is blocked.
          setTimeout(() => {
            inlineEditing.clearActiveCellId(cellId);
          }, 300);
        }
        inlineEditing.onSaveResolve(cellId, true);
        return true;
      } catch (error) {
        // Revert optimistic update
        cachedRef.current = previousCached;
        setCachedDisplayValue(previousDisplay);

        if (error instanceof Error && error.name === 'AbortError') {
          inlineEditing.onSaveResolve(cellId, false);
          return false;
        }

        if (!mountedRef.current) {
          inlineEditing.onSaveResolve(cellId, false);
          return false;
        }
        if (seq !== saveSeqRef.current || sessionAtStart !== editSessionRef.current) {
          inlineEditing.onSaveResolve(cellId, false);
          return false;
        }
        setSaveState('error');
        setIsEditing(true);
        setErrorMsg(error instanceof Error ? error.message : 'Save failed');
        inlineEditing.onSaveResolve(cellId, false);
        return false;
      } finally {
        inFlightRef.current = false;
      }
    },
    [editable, currentValue, cellId, cachedDisplayValue],
  );

  const commitValueRef = useRef<(v: unknown) => Promise<boolean>>(async () => false);
  commitValueRef.current = commitValue;

  const errorMsgRef = useRef(errorMsg);
  errorMsgRef.current = errorMsg;

  // Leaving the editor while an error is shown discards the edit; re-saving the same value would fail again.
  const leaveEditor = async (): Promise<boolean> => {
    if (errorMsgRef.current) {
      cancelEditRef.current?.();
      return true;
    }
    return commitValueRef.current(editValueRef.current);
  };
  const leaveEditorRef = useRef(leaveEditor);
  leaveEditorRef.current = leaveEditor;

  if (!editable) return null;

  const rawDisplayValue = cachedDisplayValue !== NOT_CACHED ? cachedDisplayValue : editable.rawValue;
  const displayValue: React.ReactNode =
    typeof cell.value === 'function' ? cell.value(rawDisplayValue) : (rawDisplayValue as React.ReactNode);

  const editorProps: InlineEditorProps<unknown> = {
    value: editValue,
    onChange: v => {
      setEditValue(v);
      setSaveState(prev => (prev !== 'idle' ? 'idle' : prev));
      setErrorMsg(prev => (prev ? null : prev));
    },
    onCommit: v => {
      return commitValueRef.current(v);
    },
    onCancel: cancelEdit,
    disabled: false,
    hasError: saveState === 'error',
    autoFocus: true,
    options: cell.inlineOptions || [],
    onSaveField: editable.onSaveField,
    meta: editable.meta,
  };

  const Editor = editable.editor as (props: InlineEditorProps<unknown>) => React.ReactNode;

  const resolvedEditorKey = editorKey || 'unknown';

  const paperMinWidth =
    resolvedEditorKey === 'emails' || resolvedEditorKey === 'phones' || resolvedEditorKey === 'links'
      ? 360
      : resolvedEditorKey === 'candidate'
        ? 320
        : resolvedEditorKey === 'address'
          ? 320
          : 280;

  const handleCellClick = (e: React.MouseEvent<HTMLElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('a, button, input, textarea, select, [role=button], [role=link]')) return;
    const activeId = inlineEditing.getActiveCellId();
    if (activeId && activeId !== cellId) {
      e.stopPropagation();
      void inlineEditing.commitCell(activeId);
      (e.currentTarget as HTMLElement).blur();
      return;
    }
    e.stopPropagation();
    inlineEditing.requestOpen(cellId);
  };

  const handleCellKeyDown = (e: React.KeyboardEvent<HTMLTableCellElement>) => {
    if (isEditing) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      inlineEditing.requestOpen(cellId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cellRef.current?.blur();
    }
  };

  const handleClickAway = (event: MouseEvent | TouchEvent) => {
    const target = event.target as HTMLElement;
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLInputElement && activeElement.type === 'color') {
      return;
    }
    // Ignore clicks on portaled elements that logically belong to the editor
    if (
      target.closest('input[type="color"]') ||
      target.closest('.MuiAutocomplete-popper') ||
      target.closest('.MuiPickersPopper-root') ||
      target.closest('.MuiPickersLayout-root') ||
      target.closest('[role="menu"]') ||
      target.closest('[role="listbox"]')
    ) {
      return;
    }
    void leaveEditor();
  };

  return (
    <TableCell
      ref={cellRef}
      tabIndex={-1}
      data-editing={isEditing ? 'true' : undefined}
      onClick={handleCellClick}
      onKeyDown={handleCellKeyDown}
      sx={{
        ...cellSx,
        // Sticky cells already establish a containing block for the ::after outline
        position: (cellSx as { position?: string }).position ?? 'relative',
        outline: 'none',
        overflow: 'hidden',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: '1px',
          border: `1px solid ${hoverBorderColor}`,
          borderRadius: '4px',
          pointerEvents: 'none',
          opacity: 0,
        },
        ...(true &&
          !isEditing && {
            '&:hover::after': {
              opacity: 1,
            },
            '&:focus-visible': {
              outline: `1px solid ${theme.palette.action.selected}`,
              outlineOffset: '-1px',
            },
          }),
        '&:hover .editable-pencil, &:focus-within .editable-pencil': {
          opacity: 1,
        },
        'body[data-inline-editing-active="true"] &': {
          outline: 'none',
          cursor: 'default',
          '& .editable-pencil': { opacity: 0 },
        },
        'body[data-inline-editing-active="true"] &:hover': {
          outline: 'none',
          '& .editable-pencil': { opacity: 0 },
          '&::after': { opacity: 0 },
        },
        'body[data-inline-editing-active="true"] &:focus-within': {
          outline: 'none',
          '& .editable-pencil': { opacity: 0 },
        },
      }}
    >
      <Box
        sx={{ position: 'relative', minHeight: '28px', display: 'flex', alignItems: 'center', gap: 0.5, width: '100%' }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <CellValue
            cell={{ value: displayValue }}
            columnWidthPx={columnWidthPx != null ? columnWidthPx - PENCIL_WIDTH_PX : undefined}
          />
        </Box>
        <Box
          className="editable-pencil"
          sx={{
            flexShrink: 0,
            opacity: 0,
            transition: 'opacity 0.15s',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <IconButton
            aria-label="Edit"
            size="small"
            onClick={e => {
              e.stopPropagation();
              const activeId = inlineEditing.getActiveCellId();
              if (activeId && activeId !== cellId) {
                // Another cell is active — commit it and stop.
                // User must click the pencil again to open this cell.
                void inlineEditing.commitCell(activeId);
                return;
              }
              inlineEditing.requestOpen(cellId);
            }}
            sx={{
              width: 24,
              height: 24,
              p: 0,
              border: `1px solid ${hoverBorderColor}`,
              borderRadius: '4px',
              backgroundColor: 'transparent',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          </IconButton>
        </Box>
      </Box>

      {isEditing && cellRef.current && (
        <Popper
          open={isEditing}
          anchorEl={cellRef.current}
          placement="bottom-start"
          container={() => (typeof document !== 'undefined' ? document.body : null)}
          sx={{ zIndex: theme.zIndex.inlineEditorPopper }}
          modifiers={[
            { name: 'flip', enabled: false },
            {
              name: 'offset',
              enabled: true,
              options: { offset: (data: { reference: { height: number } }) => [0, -data.reference.height] },
            },
            { name: 'preventOverflow', enabled: true, options: { padding: 8 } },
          ]}
        >
          <ClickAwayListener onClickAway={handleClickAway} disableReactTree>
            <Paper ref={paperRef} sx={{ minWidth: paperMinWidth, p: 1.5 }}>
              <Editor {...editorProps} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, gap: 1 }}>
                <Box sx={{ flex: 1 }}>
                  {errorMsg && (
                    <Typography
                      color="error"
                      variant="caption"
                      sx={{ '& a': { color: 'inherit', textDecoration: 'underline' } }}
                      dangerouslySetInnerHTML={{ __html: errorMessageHtml(errorMsg) }}
                    />
                  )}
                </Box>
              </Box>
            </Paper>
          </ClickAwayListener>
        </Popper>
      )}
    </TableCell>
  );
};

export default InlineEditableCell;
